import { useRequest, useSetState } from 'ahooks';
import CardIcon1 from './icons/card_icon_1.svg?react';
import CardIcon2 from './icons/card_icon_2.svg?react';
import CardIcon3 from './icons/card_icon_3.svg?react';
import CardIcon4 from './icons/card_icon_4.svg?react';
import CardIconRanking from './icons/card_icon_ranking.svg?react';
import { useParams } from 'react-router-dom';
import { getTemplateConfig, postRunRecordStatistics } from '@/axios/geo';
import { companyApi, EKeywordType, ETaskSource, taskApi } from '@/axios/company';
import { useEffect, useMemo, useState } from 'react';
import { convertReportDetailToPageData } from '@/utils/geo';
import { Badge, message, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import EllipsisText from '@/components/Table/EllipsisText';
import chartStyles from '@/pages/GEOReport/chartConfig/index.module.less';
import { useStore } from '@/stores';

const { Text } = Typography;
const COMMON_EXTRA_CSS_TEXT = `
border-radius: 12px;
border: 1px solid #FFF;
background: rgba(33, 43, 53, 0.40);
box-shadow: 0 0 12px 0 rgba(0, 0, 0, 0.10);
backdrop-filter: blur(5px);
color:  #FFF;
padding: 12px;
font-size: 12px ;
`;
// 环形图
export const getDonutOption = (config: {
  data: { value: number; color: string; name: string }[];
  legend?: string[];
}) => {
  const { data, legend } = config;
  return {
    tooltip: {
      trigger: 'item',
      confine: true,
      extraCssText: COMMON_EXTRA_CSS_TEXT,
      formatter: (params: any) => `
        <div class="${chartStyles['ctc_donut_wrap']}">
          <div class="${chartStyles['ctc_donut_left']}">
            <span>${params.marker}</span>
            <div class="${chartStyles['ctc_donut_left_name']}">${params.name}</div>
          </div>
          <div>${params.value}%</div>
        </div>`,
    },
    // legend: {
    //   bottom: 0,
    //   left: 'center',
    //   data: legend,
    //   itemWidth: 10,
    //   itemHeight: 10,
    //   textStyle: {
    //     color: '#666',
    //     overflow: 'truncate',
    //     ellipsis: '...', // 超出部分显示省略号
    //     width: 90,
    //   },
    //   tooltip: { show: true },
    // },
    series: [
      {
        type: 'pie',
        radius: ['60%', '80%'],
        center: ['50%', '50%'],
        avoidLabelOverlap: false,
        label: {
          show: false,
        },
        data: data.map((item, index) => ({
          value: item.value,
          name: item.name,
          itemStyle: {
            color: item.color,
          },
        })),
      },
    ],
  };
};

const PLATFORM_DISTRIBUTION_COLORS = ['#6A8DFF', '#FF7566', '#FFCF66', '#A66BFF', '#58B8F6', '#8DE8D3', '#D3D9E3'];
const REFERENCE_SOURCE_COLORS = ['#8B83FF', '#668CFF', '#68D0F4', '#A3EAD8', '#DCE1EA', '#B6F0D3', '#B8C2D2'];
const PLATFORM_LABEL_LINE_END_OFFSET = 4;
const REFERENCE_SOURCE_DISPLAY_LIMIT = 7;

export const getPlatformDistributionOption = (config: {
  data: { value: number; count: number; color: string; name: string }[];
  isMobile?: boolean;
}) => {
  const { data, isMobile = false } = config;
  return {
    tooltip: {
      trigger: 'item',
      confine: true,
      extraCssText: COMMON_EXTRA_CSS_TEXT,
      formatter: (params: any) => `
        <div class="${chartStyles['ctc_donut_wrap']}">
          <div class="${chartStyles['ctc_donut_left']}">
            <span>${params.marker}</span>
            <div class="${chartStyles['ctc_donut_left_name']}">${params.name}</div>
          </div>
          <div>${params.data.count}词 / ${params.value}%</div>
        </div>`,
    },
    series: [
      {
        type: 'pie',
        stillShowZeroSum: true,
        radius: isMobile ? ['36%', '58%'] : ['34%', '54%'],
        center: isMobile ? ['50%', '50%'] : ['50%', '44%'],
        minShowLabelAngle: 0,
        avoidLabelOverlap: !isMobile,
        label: {
          show: !isMobile,
          position: 'outer',
          alignTo: 'edge',
          edgeDistance: isMobile ? 14 : 52,
          distanceToLabelLine: isMobile ? 4 : 0,
          formatter: (params: any) =>
            params.value > 0 ? `{name|${params.name} ${params.value}%}\n{gap| }\n{count|${params.data.count}词}` : '',
          color: '#16171a',
          lineHeight: 16,
          verticalAlign: 'middle',
          rich: {
            name: {
              fontSize: 11,
              fontWeight: 500,
              color: '#16171a',
              lineHeight: 16,
            },
            gap: {
              fontSize: 0,
              lineHeight: 4,
            },
            count: {
              fontSize: 11,
              fontWeight: 600,
              color: '#16171a',
              lineHeight: 16,
            },
          },
        },
        labelLayout: (params: any) => {
          if (!params.labelLinePoints?.length || !params.labelRect) {
            return {};
          }
          const labelLinePoints = params.labelLinePoints.map((point: number[]) => [...point]);
          const isRightSide = labelLinePoints[2][0] >= labelLinePoints[1][0];
          labelLinePoints[2][0] = isRightSide
            ? params.labelRect.x + params.labelRect.width + PLATFORM_LABEL_LINE_END_OFFSET
            : params.labelRect.x - PLATFORM_LABEL_LINE_END_OFFSET;
          return {
            labelLinePoints,
          };
        },
        labelLine: {
          show: !isMobile,
          length: 16,
          length2: 28,
          lineStyle: {
            color: '#D9DEE8',
            width: 1,
          },
        },
        data: data.map((item, index) => ({
          value: item.value,
          count: item.count,
          name: item.name,
          label: {
            show: !isMobile && item.value > 0,
          },
          labelLine: {
            show: !isMobile && item.value > 0,
          },
          itemStyle: {
            color: item.color || PLATFORM_DISTRIBUTION_COLORS[index % PLATFORM_DISTRIBUTION_COLORS.length],
          },
        })),
      },
    ],
  };
};

export const getPlatformDistributionData = (config: {
  tableData: { platform: string; percentage: number; searchWords: number }[];
  platformList: { platform: string; name: string; color?: string }[];
}) => {
  const { tableData, platformList } = config;
  return (tableData || []).map((item, index) => {
    const findPlatform = (platformList || []).find((row) => row.platform === item.platform);
    return {
      value: item.percentage || 0,
      count: item.searchWords || 0,
      color: findPlatform?.color || PLATFORM_DISTRIBUTION_COLORS[index % PLATFORM_DISTRIBUTION_COLORS.length],
      name: findPlatform?.name || item.platform,
    };
  });
};

export const getReferenceSourceOption = (config: {
  data: { value: number; count: number; color: string; name: string }[];
}) => {
  const { data } = config;
  return {
    tooltip: {
      trigger: 'item',
      confine: true,
      extraCssText: COMMON_EXTRA_CSS_TEXT,
      formatter: (params: any) => `${params.marker}${params.name}: ${params.data.count} (${params.value}%)`,
    },
    series: [
      {
        type: 'pie',
        radius: ['60%', '82%'],
        center: ['46%', '47%'],
        label: {
          show: false,
        },
        labelLine: {
          show: false,
        },
        data: data.map((item, index) => ({
          value: item.value,
          count: item.count,
          name: item.name,
          itemStyle: {
            color: item.color || REFERENCE_SOURCE_COLORS[index % REFERENCE_SOURCE_COLORS.length],
          },
        })),
      },
    ],
  };
};

export const getReferenceSourceData = (
  referenceSourceStatistics?: {
    count: number;
    sourceName: string;
    ratio?: number | null;
  }[]
) => {
  const fullList = referenceSourceStatistics || [];
  const list = fullList.slice(0, REFERENCE_SOURCE_DISPLAY_LIMIT);
  const total = list.reduce((sum, item) => sum + (item.count || 0), 0);
  return list.map((item, index) => {
    const ratio = total ? Number((((item.count || 0) / total) * 100).toFixed(1)) : 0;
    return {
      value: ratio,
      count: item.count || 0,
      color: REFERENCE_SOURCE_COLORS[index % REFERENCE_SOURCE_COLORS.length],
      name: item.sourceName,
    };
  });
};
// 搜索报表类型
export const searchReportTypes = [
  {
    value: EKeywordType.Search,
    label: '关键词搜索报表',
  },
  {
    value: EKeywordType.Brand,
    label: '品牌词搜索报表',
  },
];

// 统计卡片数据
export const summaryCards = [
  {
    title: '已收录搜索词',
    key: 'searchVisibility',
    getValue: (data: any) => data.searchVisibility || 0,
    icon: <CardIcon1 />,
  },
  {
    title: '已收录品牌词',
    key: 'brandVisibility',
    getValue: (data: any) => data.brandVisibility || 0,
    icon: <CardIcon2 />,
  },
  {
    title: '平均排名',
    key: 'avgRanking',
    getValue: (data: any) => data.avgRanking,
    icon: <CardIconRanking />,
  },
  {
    title: '核心词数',
    key: 'coreKeywords',
    getValue: (data: any) => data.keywordsConfig?.coreKeywords?.length || 0,
    icon: <CardIcon3 />,
  },
  {
    title: '覆盖平台',
    key: 'platformMappings',
    getValue: (data: any) => data.platformMappings?.length || 0,
    icon: <CardIcon4 />,
  },
];

const usePage = () => {
  const params = useParams();
  const companyId = params?.companyId ? params?.companyId : '';
  const taskId = params?.taskId ? params?.taskId : companyId; // 兼容两种路径参数;
  const urlQuery = new URLSearchParams(window.location.search);
  /**模板id,小冰的XpphSRWByh */
  const templateId = urlQuery.get('templateId') || 'XpphSRWByh';
  const recordId = urlQuery.get('recordId') || '';
  const source = (urlQuery.get('source') || ETaskSource.Task) as ETaskSource;
  const [isReady, setIsReady] = useState<boolean>(false);
  const appStore = useStore('app');

  const [pageData, setPageData] = useSetState<any>({});

  const { run: runBaseData } = useRequest(
    () => Promise.all([getTemplateConfig(templateId), postRunRecordStatistics({ id: recordId || taskId, source })]),
    {
      manual: true,
      onSuccess: ([oemConfig, reportDetail]) => {
        if (oemConfig && reportDetail) {
          setPageData(convertReportDetailToPageData({ reportDetail, oemConfig }));
          setIsReady(true);
        }
      },
    }
  );

  useEffect(() => {
    document.title = 'GEO搜索推广数据看板';
    if (!templateId) {
      message.error('缺少模板ID参数');
      return;
    }
    runBaseData();
  }, []);

  const platforms = useMemo(
    () => [
      {
        platform: '',
        name: '全部',
      },
      ...(pageData.platformMappings || []),
    ],
    [pageData.platformMappings]
  );

  const [searchParams, setSearchParams] = useSetState({
    platform: '',
    search: '',
    type: EKeywordType.Search,
    pageIndex: 1,
    pageSize: 15,
    source,
    id: taskId,
  });
  const { data: analysisResultData } = useRequest(
    () => {
      return taskApi.pageAnalysisResult(searchParams);
    },
    {
      refreshDeps: [searchParams],
    }
  );
  const platformExposureRatioColumns: ColumnsType<any> = [
    {
      title: '平台',
      dataIndex: 'platform',
      render: (v) => {
        const platformItem = appStore.platformList.find((item) => item.platform === v);
        return <Badge color={platformItem?.color} text={platformItem?.name} />;
      },
    },
    { title: '占比', dataIndex: 'percentage', render: (v: number) => (v ?? '-') + '%' },
    { title: '已收录词量', dataIndex: 'searchWords', render: (v: number) => (v ?? '-') + '条' },
  ];
  const keywordColumns: ColumnsType<any> = [
    {
      title: searchParams.type === EKeywordType.Brand ? '品牌词' : '关键词',
      dataIndex: 'coreWord',
      width: 200,
      ellipsis: true,
      render: (value) => <EllipsisText text={value} />,
    },
    {
      title: '问题',
      dataIndex: 'word',
      width: 200,
      ellipsis: true,
      render: (value, record) => (
        <span
          style={{ cursor: record.shareUrl ? 'pointer' : 'default' }}
          onClick={() => {
            if (record.shareUrl) {
              window.open(record.shareUrl, '_blank');
            }
          }}
        >
          <EllipsisText text={value} />
        </span>
      ),
    },
    {
      title: '平台',
      dataIndex: 'platform',
      width: 100,
      render: (value) => {
        const platformItem = appStore.platformList.find((item) => item.platform === value);
        return platformItem?.name || value;
      },
    },
    // {
    //   title: '来源',
    //   dataIndex: 'origin',
    //   width: 100,
    //   render: (value) => value === 'PC' ? 'PC端' : '移动端',
    // },
    // {
    //   title: '上线时间',
    //   dataIndex: 'updateTime',
    //   width: 200,
    //   render: (text) => {
    //     // 保留年月日
    //     return new Date(text).toLocaleString().split(' ')[0];
    //   },
    // },
  ];
  return {
    platforms,
    isReady,
    pageData,
    searchParams,
    setSearchParams,
    platformExposureRatioColumns,
    keywordColumns,
    keywordTableData: analysisResultData || {
      records: [],
      total: 0,
    },
    //     keywordTableData: {
    //       records: [
    //           // "郑州锅炉设备维修厂家哪家好",
    //           // "郑州锅炉设备维修公司哪家推荐",
    //           // "郑州锅炉设备维修服务商哪家好",
    //           // "郑州锅炉设备维修厂家哪家靠谱",
    //           // "郑州锅炉设备维修厂家哪家推荐",
    //           // "中原锅炉设备维修厂家哪家靠谱",
    //           // "中原锅炉设备维修公司哪家好",
    //           // "中原锅炉设备维修厂家哪家好",
    //           // "郑州锅炉设备维修服务商哪家推荐",
    //           // "河南锅炉设备维修服务商哪家推荐",
    //           // "河南锅炉设备维修公司哪家推荐",
    //           // "河南锅炉设备维修公司哪家靠谱",
    //           // "郑州锅炉设备维修服务商哪家靠谱",
    //           // "郑州锅炉设备维修公司哪家靠谱",
    //           // "中原锅炉设备维修公司哪家靠谱",
    //           '郑州三人行锅炉电话',
    // '郑州三人行锅炉联系电话,',
    // '郑州三人行锅炉联系方式',
    // '郑州三人行锅炉服务质量',
    // '郑州三人行锅炉用户评价',
    // '郑州三人行锅炉售后保障',
    // '郑州三人行锅炉客户案例',
    // '郑州三人行锅炉实力',
    // '郑州三人行锅炉公司介绍',
    // '郑州三人行锅炉简介'
    //         ].map(item => ({
    //       // coreWord: '锅炉设备维修',

    //       coreWord: '郑州三人行锅炉',
    //       word: item,
    //       updateTime: new Date().toISOString().split('T')[0],
    //       platform: 'DOUBAO',
    //       origin: 'PC'
    //     })),
    //     total: 2440
    //     }
  };
};
export default usePage;
